import { ApexSign, MechanicalAxisFemurAP, SelectedApexMech, VectorUtils, Consts } from '@ortho-next/nextray-core';
import { MathUtils, Vector3 } from 'three';
import { ToolsAP, ToolsLT } from '../Core/Tools';
import { OsteotomyCut } from '../States/State';
import { MechanicalAxisAP } from '../Tools/DeformityAnalyzer/FullAnalyzerAP';
import { MechanicalAxisLT } from '../Tools/DeformityAnalyzer/FullAnalyzerLT';
import { EoCPlane } from '../Tools/EoCPlane/EoCPlane';
import { Osteotomy } from '../Tools/Osteotomy/Osteotomy';
import { OsteotomyAP } from '../Tools/Osteotomy/OsteotomyAP';

/**
 * Contains formulas for calculating measurements using multiple tools.
 */
export class MeasuresUtils {

	/**
	 * Calculates the horizontal translation that will be applied after correction.
	 */
	public static getHorizontalTranslation(ost: Osteotomy, mechAxis: MechanicalAxisAP | MechanicalAxisLT, rotationCenter: Vector3): number {
		const isTibia = ost.selectedApex === SelectedApexMech.tibiaProximal || ost.selectedApex === SelectedApexMech.tibiaDistal;
		if (mechAxis instanceof MechanicalAxisLT && !isTibia) {
			return 0;
		}
		const mechSide = isTibia ? mechAxis.tibia! : mechAxis.femur! as MechanicalAxisFemurAP;
		const angle = mechSide.apexWithSign;
		const apex = mechSide instanceof MechanicalAxisFemurAP ? mechSide.mechanical.apex.position : mechSide.apex.position;
		const bisector = mechSide.apexBisector;
		const apexAngle = mechSide.apexAngleToFix.value!;
		let distanceRotCenterFromBisector;

		if (apexAngle <= Math.PI / 2) {
			const projectedRotationCenter = VectorUtils.projectOnVector(rotationCenter, apex, apex.clone().add(bisector));
			distanceRotCenterFromBisector = projectedRotationCenter.distanceTo(rotationCenter);
		} else { //apex out of joints
			const projectedRotationCenter = VectorUtils.projectOnVector(rotationCenter, apex, apex.clone().add(bisector));
			distanceRotCenterFromBisector = projectedRotationCenter.distanceTo(apex);
		}


		// Calculate sign
		let sign = 1;
		if (apexAngle <= Math.PI / 2) { // apex inside joints
			const isUpperBisector = VectorUtils.arePointsOnSameSide(apex, apex.clone().add(bisector), Consts.planeNormal, apex.clone().add(Consts.verDir), rotationCenter);
			sign = isUpperBisector ? -1 : 1;
		} else { // apex ouside joints
			const isUpperApex = VectorUtils.arePointsOnSameSide(apex, apex.clone().add(Consts.horDir), Consts.planeNormal, apex.clone().add(Consts.verDir), rotationCenter);
			sign = isUpperApex ? -1 : 1;
		}

		return distanceRotCenterFromBisector * Math.tan((angle) / -2) * 2 * sign;
	}

	/**
	 * Calculates the EOC vertical translation tha will be applied in both views.
	 */
	public static getEoCVerticalTranslationBeforeCut(toolsAP: ToolsAP, toolsLT: ToolsLT, cutType: OsteotomyCut): number {
		const osteotomyLength = Math.max(toolsAP.osteotomy ? toolsAP.osteotomy.length : 0, toolsLT.osteotomy ? toolsLT.osteotomy.length : 0);
		const angleAP = (toolsAP.mechanicalAxis && toolsAP.osteotomy) ? toolsAP.mechanicalAxis.getApexWithSign(toolsAP.osteotomy.selectedApex as SelectedApexMech) : 0;
		const angleLT = (toolsLT.mechanicalAxis && toolsLT.osteotomy) ? toolsLT.mechanicalAxis.getApexWithSign(toolsLT.osteotomy.selectedApex as SelectedApexMech) : 0;
		const sign = cutType == OsteotomyCut.closing ? -1 : 1;
		return Math.sin(Math.atan(Math.sqrt(Math.tan(angleAP) ** 2 + Math.tan(angleLT) ** 2))) * osteotomyLength / 2 * sign;
	}

	/**
	 * Calculates the cortex wire used in report to print only.
	 */
	public static getCortexWire(mechanicalAxis: MechanicalAxisAP, osteotomy: OsteotomyAP, selectedApex: SelectedApexMech, wireOrigin: Vector3): number | undefined {
		if (selectedApex == SelectedApexMech.femurProximal || selectedApex == SelectedApexMech.femurDistal) {
			const refPoint = this.getReferencePointToProximalPrintMeasure(mechanicalAxis, osteotomy);
			return refPoint.distanceTo(wireOrigin.setZ(0));
		}
		return undefined;
	}

	/**
	* Calculates the osteotomy level used in report to print only.
	*/
	public static getOsteotomyLevel(mechanicalAxis: MechanicalAxisAP, osteotomy: OsteotomyAP, selectedApex: SelectedApexMech): number | undefined {
		switch (selectedApex) {
			case SelectedApexMech.femurProximal: {
				const refPoint = this.getReferencePointToProximalPrintMeasure(mechanicalAxis, osteotomy);
				return refPoint.distanceTo(mechanicalAxis.femur.mechanical.GT.position);
			}
			case SelectedApexMech.femurDistal: {
				const refPoint = this.getReferencePointToProximalPrintMeasure(mechanicalAxis, osteotomy);
				return refPoint.distanceTo(mechanicalAxis.femur.mechanical.LE.position);
			}
			case SelectedApexMech.tibiaProximal:
				return mechanicalAxis.tibia.CP.position.distanceTo(osteotomy.C.position);
			case SelectedApexMech.tibiaDistal:
				return mechanicalAxis.tibia.CA.position.distanceTo(osteotomy.C.position);
		}
		return undefined;
	}

	/**
	 * Calculates the wire insertion angle used in report to print only.
	 */
	public static getWireInsertionAngle(mechanicalAxis: MechanicalAxisAP, EOCplane: EoCPlane, wireAngleDegrees: number, selectedApex: SelectedApexMech): number | undefined {
		if (selectedApex == SelectedApexMech.femurProximal || selectedApex == SelectedApexMech.femurDistal) {
			const wedge = EOCplane.angle;
			const sign = mechanicalAxis.femur.apexAngleSign.value === ApexSign.valgus ? 1 : -1;
			return MathUtils.degToRad(wireAngleDegrees) + (wedge * sign);
		}
		return undefined;
	}

	private static getReferencePointToProximalPrintMeasure(mechanicalAxis: MechanicalAxisAP, osteotomy: OsteotomyAP): Vector3 {
		const sideAxis = Consts.horDir.clone().multiplyScalar(mechanicalAxis.isLeft ? 1 : -1).applyAxisAngle(Consts.planeNormal, mechanicalAxis.femur.angleForTemplating);
		const Asign = VectorUtils.computeSign(osteotomy.A.position, osteotomy.C.position, sideAxis);
		return Asign === 1 ? osteotomy.A.position : osteotomy.B.position;
	}
}
